import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { Badge, Tooltip } from "../../../../utils/local-antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useState } from "react";

import { Button } from "../../../../components/Button";
import { CopyButton } from "../../../../components/CopyButton";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { IntegrationSection } from "../IntegrationSection";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { useNavigate } from "react-router-dom";
import { faLinkSlash } from "@fortawesome/free-solid-svg-icons/faLinkSlash";
import { WarningModal } from "../../../../components/WarningModal";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { useTheme } from "../../../../ThemeContext";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { Spacing } from "../../../../theme/Spacing";
import { TestIDs } from "../../../../tests/types/testIds";
import { WAAIAgentDrawer } from "../whatsapp-chat/WAAIAgentDrawer";
import { WAAutoAssignDrawer } from "../whatsapp-chat/WAAutoAssignDrawer";
import { WANumberHealth } from "../whatsapp-chat/WANumberHealth";
import { WAMediaStorage } from "../whatsapp-chat/WAMediaStorage";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";

const StyledButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  color: ${({ theme }) => theme.main};
  margin-inline-start: auto;
`;

const AgentButton = styled(StyledButton)<{ $active: boolean }>`
  color: ${({ theme, $active }) => ($active ? theme.success : theme.main)};
`;

export const IntegrationWhatsAppMetaSection: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isAIAgentDrawerOpen, setIsAIAgentDrawerOpen] = useState(false);
  const [isAutoAssignDrawerOpen, setIsAutoAssignDrawerOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();

  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const totalUnread = useAppSelector(
    integrationSliceSelectors.selectTotalUnreadWhatsAppMetaChats,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onDisconnectIntegration = async () => {
    try {
      setLoading(true);
      await dispatch(
        integrationActions.disconnectIntegration({
          integrationId: integration?._id!,
          userId,
        }),
      ).unwrap();

      await dispatch(
        integrationActions.getUserIntegrations({ userId }),
      ).unwrap();
      AtiraToast.success(
        t("integration.whatsapp.meta.disconnect.modal.success"),
      );
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  if (!integration) {
    return null;
  }

  const isEnabled = integration.status.enabled;

  return (
    <IntegrationSection
      enabled={isEnabled}
      icon={faWhatsapp}
      iconColor="whatsapp"
    >
      <Flex
        gap="s"
        alignItems="center"
        justifyContent="space-between"
        flex={1}
        dataTestId={TestIDs.integration.whatsappMeta.section.wrapper}
      >
        <Flex gap="m" alignItems="center">
          <Text>{t("integration.add-modal.whatsapp.meta.button")}</Text>

          <Flex marginInlineStart="auto" gap="s" alignItems="center">
            <Text
              color="darkerSub"
              direction="ltr"
              style={{
                unicodeBidi: "isolate",
                display: "flex",
                alignItems: "center",
                gap: Spacing.xs,
              }}
            >
              <u>{integration.config.phoneNumber}</u>
              {integration.config.phoneNumber ? (
                <CopyButton
                  copy={integration.config.phoneNumber}
                  iconSize="sm"
                />
              ) : null}
              <Tooltip
                title={t("integration.whatsapp.meta.phoneNumber.tooltip")}
              >
                <AtiraIcon icon={faInfoCircle} size="xs" />
                <span />
              </Tooltip>
            </Text>
            <Text color="darkerSub">-</Text>
            <Text
              color="darkerSub"
              direction="ltr"
              style={{
                unicodeBidi: "isolate",
                display: "flex",
                alignItems: "center",
                gap: Spacing.xs,
              }}
            >
              {integration.config.pinCode}
              <Tooltip title={t("integration.whatsapp.meta.pinCode.tooltip")}>
                <AtiraIcon icon={faInfoCircle} size="xs" />
                <span />
              </Tooltip>
            </Text>

            <WANumberHealth />
            <WAMediaStorage />
          </Flex>
        </Flex>

        <Flex gap="s" alignItems="center">
          <Tooltip title={t("integration.whatsapp.meta.disconnect.tooltip")}>
            <StyledButton
              icon={faLinkSlash}
              onClick={() => setIsDisconnectModalOpen(true)}
              iconWidth="2x"
              dataTestId={
                TestIDs.integration.whatsappMeta.section.disconnectButton
              }
            />
          </Tooltip>

          <Tooltip
            title={
              integration.config.aiAgent?.enabled
                ? t("integration.whatsapp.meta.aiAgent.tooltip.on", {
                    name:
                      integration.config.aiAgent?.name ||
                      t("integration.whatsapp.meta.aiAgent.defaultName"),
                  })
                : t("integration.whatsapp.meta.aiAgent.tooltip.off")
            }
          >
            <AgentButton
              icon={faRobot}
              onClick={() => setIsAIAgentDrawerOpen(true)}
              iconWidth="2x"
              $active={Boolean(integration.config.aiAgent?.enabled)}
              dataTestId={
                TestIDs.integration.whatsappMeta.section.aiAgentButton
              }
            />
          </Tooltip>

          <Tooltip
            title={
              integration.config.autoAssign?.enabled
                ? t("integration.whatsapp.meta.autoAssign.tooltip.on", {
                    count: integration.config.autoAssign.assigneeIds.length,
                  })
                : t("integration.whatsapp.meta.autoAssign.tooltip.off")
            }
          >
            <AgentButton
              icon={faUserPlus}
              onClick={() => setIsAutoAssignDrawerOpen(true)}
              iconWidth="2x"
              $active={Boolean(integration.config.autoAssign?.enabled)}
            />
          </Tooltip>

          <Tooltip title={t("common.view")}>
            <Badge
              count={totalUnread}
              size="small"
              color={theme.main}
              overflowCount={99}
              offset={[0, 15]}
            >
              <StyledButton
                icon={faEye}
                onClick={() => navigate(`/integration/whatsapp-meta`)}
                iconWidth="2x"
                dataTestId={TestIDs.integration.whatsappMeta.section.viewButton}
              />
            </Badge>
          </Tooltip>
        </Flex>
      </Flex>

      <WAAIAgentDrawer
        open={isAIAgentDrawerOpen}
        onClose={() => setIsAIAgentDrawerOpen(false)}
      />

      <WAAutoAssignDrawer
        open={isAutoAssignDrawerOpen}
        onClose={() => setIsAutoAssignDrawerOpen(false)}
      />

      <WarningModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={onDisconnectIntegration}
        title={t("integration.whatsapp.meta.disconnect.modal.title")}
        description={t(
          "integration.whatsapp.meta.disconnect.modal.description",
        )}
        confirmButtonText={t("integration.whatsapp.meta.disconnect.confirm")}
        confirmButtonDataTestId={
          TestIDs.integration.whatsappMeta.section.disconnectConfirm
        }
        loading={loading}
      />
    </IntegrationSection>
  );
};
